/**
 * /tools/masterpiece-blueprint?clientId=X — batch #4 (#6).
 *
 * The combined "Masterpiece Day & Energy Audit" tool: one landing, two parts.
 *   - Part 1 → Energy Audit (`/tools/energy-audit`)
 *   - Part 2 → Masterpiece Day (`/tools/masterpiece-day`)
 *
 * Shows each part's completion status. Both parts persist under their own
 * tool keys, so the coach still sees two distinct payloads.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { PrintButton } from '@/components/print-button';

export function MasterpieceBlueprintPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [part1Done, setPart1Done] = useState(false);
  const [part2Done, setPart2Done] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) return;
    void (async () => {
      try {
        const [audit, day] = await Promise.all([
          api.getToolCompletion(accessToken, clientId, 'energy-audit').catch(() => null),
          api.getToolCompletion(accessToken, clientId, 'masterpiece-day').catch(() => null),
        ]);
        if (!active) return;
        setPart1Done(audit?.status === 'completed');
        setPart2Done(day?.status === 'completed');
      } catch {
        /* swallow — status is a nicety, not load-bearing */
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="masterpiece-blueprint-missing-client">
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.masterpieceBlueprint.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">{t('tools.masterpieceBlueprint.missingClient')}</p>
      </section>
    );
  }

  const cid = encodeURIComponent(clientId);

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.masterpieceBlueprint.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.masterpieceBlueprint.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.masterpieceBlueprint.description')}
        </p>
      </header>

      <div className="grid gap-s4 sm:grid-cols-2">
        <PartCard
          to={`/tools/energy-audit?clientId=${cid}`}
          label={t('tools.masterpieceBlueprint.part1.label')}
          hint={t('tools.masterpieceBlueprint.part1.hint')}
          cta={t('tools.masterpieceBlueprint.part1.cta')}
          done={part1Done}
          doneLabel={t('tools.masterpieceBlueprint.done')}
          notStartedLabel={t('tools.masterpieceBlueprint.notStarted')}
          testId="masterpiece-blueprint-part1"
        />
        <PartCard
          to={`/tools/masterpiece-day?clientId=${cid}`}
          label={t('tools.masterpieceBlueprint.part2.label')}
          hint={t('tools.masterpieceBlueprint.part2.hint')}
          cta={t('tools.masterpieceBlueprint.part2.cta')}
          done={part2Done}
          doneLabel={t('tools.masterpieceBlueprint.done')}
          notStartedLabel={t('tools.masterpieceBlueprint.notStarted')}
          testId="masterpiece-blueprint-part2"
          locked={!part1Done && !part2Done}
          lockedLabel={t('tools.masterpieceBlueprint.locked')}
          lockedHint={t('tools.masterpieceBlueprint.lockedHint')}
        />
      </div>
    </section>
  );
}

function PartCard({
  to,
  label,
  hint,
  cta,
  done,
  doneLabel,
  notStartedLabel,
  testId,
  locked = false,
  lockedLabel,
  lockedHint,
}: {
  to: string;
  label: string;
  hint: string;
  cta: string;
  done: boolean;
  doneLabel: string;
  notStartedLabel: string;
  testId: string;
  locked?: boolean;
  lockedLabel?: string;
  lockedHint?: string;
}): JSX.Element {
  return (
    <article
      className="flex flex-col rounded-lg border border-bone-3 bg-bone-2 p-s5"
      data-testid={testId}
      data-locked={locked ? 'true' : 'false'}
    >
      <div className="flex items-start justify-between gap-s2">
        <h2 className="font-display text-display-sm text-ink">{label}</h2>
        <span
          className={
            'shrink-0 rounded-full px-s2 py-0.5 font-mono text-[10px] uppercase tracking-wide ' +
            (done
              ? 'bg-semantic-success/15 text-semantic-success'
              : locked
                ? 'bg-bone-3 text-gray-1'
                : 'bg-bone-3 text-gray-2')
          }
          data-testid={`${testId}-status`}
        >
          {done ? `✓ ${doneLabel}` : locked ? `🔒 ${lockedLabel ?? notStartedLabel}` : notStartedLabel}
        </span>
      </div>
      <p className="mt-s2 flex-1 text-sm text-gray-2">{locked ? (lockedHint ?? hint) : hint}</p>
      {locked ? (
        <button
          type="button"
          disabled
          className="mt-s4 w-full rounded-md px-s5 py-s3 text-center font-semibold text-gray-2 bg-bone-3 cursor-not-allowed"
          data-testid={`${testId}-open`}
        >
          {cta}
        </button>
      ) : (
        <Link to={to} className="btn-primary mt-s4 w-full text-center" data-testid={`${testId}-open`}>
          {cta}
        </Link>
      )}
    </article>
  );
}
